import React, { useEffect, useState } from 'react';
import { useEnvironment } from '../utils/EnvironmentContext';
import { getApiBaseUrl } from '../api/client';

// const API_URL = 'http://localhost:3000/api/twilio-phone-numbers';
const MEMO_ORANGE = '#FF6600';
const MEMO_BLUE = '#2d4a7a';
const MEMO_GREEN = '#2d7a2d';

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  const header = lines[0].split(';');
  const e164Idx = header.indexOf('E164 Number');
  const clientIdx = header.indexOf('Client');
  if (e164Idx === -1) return { used: [], available: [] };
  const used = [];
  const available = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(';');
    const number = cols[e164Idx];
    const client = cols[clientIdx];
    if (!number) continue;
    if (client && client.trim()) {
      used.push({ number, client });
    } else {
      available.push(number);
    }
  }
  return { used, available };
}

function Telefoonnummers() {
  // Twilio logic
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  // Remove showConsecutive filter
  // const [flowNames, setFlowNames] = useState({});
  const { env = 'memo' } = useEnvironment?.() || {};

  // COLT logic
  const [coltUsedNumbers, setColtUsedNumbers] = useState([]);
  const [coltAvailableNumbers, setColtAvailableNumbers] = useState([]);
  const [coltLoading, setColtLoading] = useState(true);
  const [coltError, setColtError] = useState(null);

  function getNumericPart(num) {
    return num.replace(/[^0-9]/g, '');
  }
  function extractFlowSid(url) {
    if (!url) return null;
    const match = url.match(/Flows\/(FW[0-9a-f]+)/i);
    return match ? match[1] : null;
  }
  function groupConsecutiveNumbers(numbers) {
    if (!numbers.length) return [];
    const sorted = [...numbers].sort((a, b) => {
      return parseInt(getNumericPart(a.phoneNumber)) - parseInt(getNumericPart(b.phoneNumber));
    });
    const groups = [];
    let currentGroup = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      const prevNum = parseInt(getNumericPart(sorted[i - 1].phoneNumber));
      const currNum = parseInt(getNumericPart(sorted[i].phoneNumber));
      if (currNum === prevNum + 1) {
        currentGroup.push(sorted[i]);
      } else {
        if (currentGroup.length > 1) groups.push(currentGroup);
        currentGroup = [sorted[i]];
      }
    }
    if (currentGroup.length > 1) groups.push(currentGroup);
    return groups.sort((a, b) => a.length - b.length);
  }

  useEffect(() => {
    async function fetchNumbers() {
      setLoading(true);
      // setError(null);
      try {
        const url = `${getApiBaseUrl()}/twilio-phone-numbers?env=${env}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch phone numbers');
        const data = await res.json();
        setNumbers(data);
      } catch (err) {
        // setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchNumbers();
  }, [env]);

  useEffect(() => {
    async function fetchCSV() {
      setColtLoading(true);
            // setError(err.message);
      try {
        const res = await fetch('/unusedRanges(Comparison).csv');
        if (!res.ok) throw new Error('Failed to fetch CSV');
        const text = await res.text();
        const { used, available } = parseCSV(text);
        setColtUsedNumbers(used);
        setColtAvailableNumbers(available);
      } catch (err) {
        setColtError(err.message);
      } finally {
        setColtLoading(false);
      }
    }
    fetchCSV();
  }, []);

  const filteredNumbers = numbers.filter(num =>
    num.phoneNumber?.toLowerCase().includes(filter.toLowerCase()) ||
    num.friendlyName?.toLowerCase().includes(filter.toLowerCase())
  );
  const availableNumbers = filteredNumbers.filter(num => !num.voiceUrl && !num.smsUrl);
  const [assigningSid, setAssigningSid] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignName, setAssignName] = useState("");
  const [assignError, setAssignError] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState(false);
  // Memo-Flow SID (fetched from backend for safety)
  const [memoFlowSid, setMemoFlowSid] = useState("");
  useEffect(() => {
    async function fetchFlowSid() {
      try {
        const res = await fetch(`${getApiBaseUrl()}/twilio-flow-sid?env=${env}`);
        if (res.ok) {
          const data = await res.json();
          setMemoFlowSid(data.flowSid);
        }
      } catch {}
    }
    fetchFlowSid();
  }, [env]);
  const usedNumbers = filteredNumbers.filter(num => !!num.voiceUrl || !!num.smsUrl);
  // Always group available numbers by consecutive ranges, shortest first
  const consecutiveGroups = groupConsecutiveNumbers(availableNumbers);

  useEffect(() => {
    async function fetchFlowNames() {
      const sids = usedNumbers
        .map(num => extractFlowSid(num.voiceUrl) || extractFlowSid(num.smsUrl))
        .filter(Boolean);
      const uniqueSids = Array.from(new Set(sids));
      const names = {};
      for (const sid of uniqueSids) {
        try {
          const res = await fetch(`http://localhost:3000/api/flow-friendly-name?flowSid=${sid}&env=${env}`);
          if (res.ok) {
            const data = await res.json();
            names[sid] = data.friendlyName;
          }
        } catch {}
      }
      // setFlowNames(names);
    }
    if (usedNumbers.length > 0) fetchFlowNames();
  }, [usedNumbers, env]);

  // Filter logic for COLT
  const coltFilteredAvailable = coltAvailableNumbers.filter(num => num.toLowerCase().includes(filter.toLowerCase()));
  const coltFilteredUsed = coltUsedNumbers.filter(obj => obj.number.toLowerCase().includes(filter.toLowerCase()) || (obj.client && obj.client.toLowerCase().includes(filter.toLowerCase())));

  if (loading || coltLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="spinner" style={{ width: 60, height: 60, border: '6px solid #eee', borderTop: `6px solid ${MEMO_ORANGE}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <div style={{ marginTop: 18, color: MEMO_ORANGE, fontWeight: 700, fontSize: '1.2rem' }}>Laden telefoonnummers...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  return (
    <div style={{ padding: '2.5rem 0', maxWidth: '100vw', margin: '0 auto', background: '#f7fafd', borderRadius: 18, boxShadow: '0 4px 24px rgba(14,66,106,0.07)' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '1.2rem', color: MEMO_ORANGE, fontWeight: 800, fontSize: '2.4rem', letterSpacing: '0.5px', fontFamily: 'Wix Madefor Text, sans-serif' }}>Telefoonnummers</h2>
      <div style={{ textAlign: 'center', marginBottom: '2.2rem', color: '#d32f2f', fontWeight: 700, fontSize: '1.15rem', background: '#fff3e0', borderRadius: 8, padding: '0.8rem 1.2rem', maxWidth: 700, margin: '0 auto 2.2rem auto', boxShadow: '0 1px 6px rgba(14,66,106,0.04)' }}>
        WORK IN PROGRESS. 
        Neem contact op met het IT, vooral Dylan :) , voor vragen of feedback.
      </div>
      <div style={{ marginBottom: '2.2rem', display: 'flex', justifyContent: 'center', gap: '1.2rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Filter op nummer, naam of client..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '0.85rem 1.25rem', borderRadius: 10, border: '1.5px solid #dbe6ef', width: 340, fontSize: '1.12rem', boxShadow: '0 1px 4px rgba(14,66,106,0.04)', background: '#fff', fontFamily: 'inherit' }}
        />
      </div>
      <div style={{ display: 'flex', gap: '2.5rem', justifyContent: 'center', alignItems: 'flex-start', width: '100%', margin: '0 auto' }}>
        {/* Twilio container */}
        <section style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 8px rgba(14,66,106,0.07)', padding: '2rem 1.2rem', flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', color: MEMO_ORANGE, fontWeight: 800, fontSize: '1.35rem', letterSpacing: '0.5px', borderBottom: '2px solid #ffe2d1', paddingBottom: '0.5rem' }}>Twilio Phone Numbers</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', minWidth: 0 }}>
            {/* Beschikbare nummers */}
            <div style={{ minWidth: 0 }}>
              <h4 style={{ textAlign: 'center', marginBottom: '1.2rem', color: MEMO_GREEN, fontWeight: 800, fontSize: '1.13rem', letterSpacing: '0.5px', borderBottom: '2px solid #eafbe7', paddingBottom: '0.5rem' }}>Beschikbare nummers</h4>
              <div style={{ borderRadius: 10, overflow: 'visible', maxWidth: '100%' }}>
                <table style={{ width: '100%', maxWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '1.09rem', tableLayout: 'auto' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr style={{ background: '#f7f7f7' }}>
                      <th style={{ textAlign: 'left', padding: '1rem', borderBottom: '2px solid #eaeaea', fontWeight: 700, whiteSpace: 'nowrap' }}>Phone Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consecutiveGroups.length === 0 ? (
                      <tr><td style={{ textAlign: 'center', padding: '2rem', color: '#888', fontWeight: 500 }}>Geen beschikbare nummers.</td></tr>
                    ) : (
                      consecutiveGroups.map((group, idx) => (
                        <React.Fragment key={idx}>
                          <tr style={{ background: '#eafbe7' }}>
                            <td colSpan={1} style={{ fontWeight: 700, padding: '1rem', color: MEMO_GREEN, fontSize: '1.07rem' }}>
                              {group.length === 1 ? 'Single number' : `Opeenvolgende groep (${group.length} nummers)`}
                            </td>
                          </tr>
                          {group.map((num, i) => (
                            <tr key={num.sid || num.phoneNumber} style={{ background: i % 2 === 0 ? '#fff' : '#f6fafd', borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '1rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{num.phoneNumber}</span>
                                {num.sid && (
                                  <button
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: MEMO_ORANGE,
                                      padding: '0.2em',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                      transition: 'background 0.15s',
                                      marginLeft: 12,
                                      width: 28,
                                      height: 28,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = '#fff3e0'}
                                    onMouseOut={e => e.currentTarget.style.background = 'none'}
                                    onClick={() => {
                                      setAssigningSid(num.sid);
                                      setAssignModalOpen(true);
                                      setAssignName("");
                                      setAssignError("");
                                      setAssignSuccess(false);
                                    }}
                                    aria-label="Nummer toewijzen"
                                  >
                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="10" cy="10" r="9" stroke={MEMO_ORANGE} strokeWidth="2" fill="none" />
                                      <g stroke={MEMO_ORANGE} strokeWidth="1.5" fill="none">
                                        <circle cx="10" cy="10" r="3.2" />
                                        <path d="M10 5.5v1.2M10 13.3v1.2M5.5 10h1.2M13.3 10h1.2M7.2 7.2l.85.85M12 12l.85.85M7.2 12.8l.85-.85M12 8l.85-.85" strokeLinecap="round" />
                                      </g>
                                    </svg>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Gebruikte nummers */}
            <div style={{ minWidth: 0 }}>
              <h4 style={{ textAlign: 'center', marginBottom: '1.2rem', color: MEMO_BLUE, fontWeight: 800, fontSize: '1.13rem', letterSpacing: '0.5px', borderBottom: '2px solid #e2eafc', paddingBottom: '0.5rem' }}>Gebruikte nummers</h4>
              <div style={{ borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '1.09rem', tableLayout: 'auto' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr style={{ background: '#f7f7f7' }}>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Phone Number</th>
                      <th style={{ textAlign: 'left', padding: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Friendly Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usedNumbers.length === 0 ? (
                      <tr><td colSpan={2} style={{ textAlign: 'center', padding: '2rem', color: '#888', fontWeight: 500 }}>Geen gebruikte nummers.</td></tr>
                    ) : (
                      usedNumbers.map((num, i) => (
                        <tr key={num.sid} style={{ background: i % 2 === 0 ? '#fff' : '#f6fafd', borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '1rem', whiteSpace: 'nowrap' }}>{num.phoneNumber}</td>
                          <td style={{ padding: '1rem', whiteSpace: 'nowrap' }}>{num.friendlyName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
        {/* COLT container */}
        <section style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 8px rgba(14,66,106,0.07)', padding: '2rem 1.2rem', flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#da681f', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '0.5px', borderBottom: '2px solid #ffe2d1', paddingBottom: '0.5rem' }}>COLT Phone Numbers</h3>
          {coltLoading && <div style={{ textAlign: 'center', padding: '2rem', color: '#da681f', fontWeight: 700 }}>Loading...</div>}
          {coltError && <div style={{ color: 'red', textAlign: 'center', fontWeight: 700 }}>Error: {coltError}</div>}
          {!coltLoading && !coltError && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', minWidth: 0 }}>
              <div style={{ minWidth: 0 }}>
                <h4 style={{ textAlign: 'center', marginBottom: '1.2rem', color: MEMO_GREEN, fontWeight: 800, fontSize: '1.13rem', letterSpacing: '0.5px', borderBottom: '2px solid #eafbe7', paddingBottom: '0.5rem' }}>Beschikbare nummers</h4>
                <div style={{ borderRadius: 10, overflow: 'visible', maxWidth: '100%' }}>
                  {/* Group COLT available numbers by range, show untouched message if needed */}
                  <table style={{ width: '100%', maxWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '1.09rem', tableLayout: 'auto' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                      <tr style={{ background: '#f7f7f7' }}>
                        <th style={{ textAlign: 'left', padding: '1rem', borderBottom: '2px solid #eaeaea', fontWeight: 700, whiteSpace: 'nowrap' }}>Phone Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Group by prefix (first 5 digits)
                        const rangeGroups = {};
                        coltFilteredAvailable.forEach(num => {
                          const prefix = num.slice(0, 5);
                          if (!rangeGroups[prefix]) rangeGroups[prefix] = [];
                          rangeGroups[prefix].push(num);
                        });
                        // Find untouched ranges (no used numbers in that prefix)
                        const usedNumbersByPrefix = {};
                        coltFilteredUsed.forEach(obj => {
                          const prefix = obj.number.slice(0, 5);
                          if (!usedNumbersByPrefix[prefix]) usedNumbersByPrefix[prefix] = [];
                          usedNumbersByPrefix[prefix].push(obj.number);
                        });
                        return Object.entries(rangeGroups)
                          .sort((a, b) => a[1].length - b[1].length)
                          .map(([prefix, nums], idx) => {
                            // DDI range: show min-max in group
                            const sortedNums = nums.slice().sort();
                            const ddiStart = Number(sortedNums[0]);
                            const ddiEnd = Number(sortedNums[sortedNums.length - 1]);
                            // Find used numbers in this range
                            const usedInRange = (usedNumbersByPrefix[prefix] || []).filter(n => {
                              const nNum = Number(n);
                              return nNum >= ddiStart && nNum <= ddiEnd;
                            });
                            const totalInRange = nums.length + usedInRange.length;
                            const allAvailable = usedInRange.length === 0;
                            // Group header row
                            return (
                              <React.Fragment key={prefix}>
                                <tr>
                                  <td colSpan={1} style={{ background: '#eafbe7', width: '100%', fontWeight: 700, padding: '1rem', color: MEMO_GREEN, fontSize: '1.07rem', borderRadius: 8, marginBottom: '-0.5rem' }}>
                                    {(allAvailable && nums.length > 1)
                                      ? <span style={{ color: '#d32f2f', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}><span style={{ fontSize: '1.2em', marginRight: '0.4em' }}>❗</span>Gebruik eerst een nummer van een andere groep AUB</span>
                                      : 'Opeenvolgende groep'}<br />
                                    <span style={{ fontWeight: 500, color: '#2d7a2d', fontSize: '0.98em' }}>DDI range: {sortedNums[0]} - {sortedNums[sortedNums.length - 1]} &nbsp; | &nbsp; Beschikbaar: {nums.length} / Totaal: {totalInRange}</span>
                                  </td>
                                </tr>
                                {nums.map((num, i) => (
                                  <tr key={num} style={{ background: i % 2 === 0 ? '#fff' : '#f6fafd', borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '1rem', whiteSpace: 'nowrap' }}>{num}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <h4 style={{ textAlign: 'center', marginBottom: '1.2rem', color: '#0e426a', fontWeight: 800, fontSize: '1.13rem', letterSpacing: '0.5px', borderBottom: '2px solid #e2eafc', paddingBottom: '0.5rem' }}>Gebruikte nummers</h4>
                <div style={{ overflowX: 'auto', borderRadius: 10 }}>
                  <table style={{ width: '100%', maxWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '1.09rem', tableLayout: 'auto' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                      <tr style={{ background: '#f7f7f7' }}>
                        <th style={{ textAlign: 'left', padding: '1rem', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 120 }}>Friendly Name</th>
                        <th style={{ textAlign: 'left', padding: '1rem', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 120 }}>Client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coltFilteredUsed.length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign: 'center', padding: '2rem', color: '#888', fontWeight: 500 }}>Geen gebruikte nummers.</td></tr>
                      ) : (
                        coltFilteredUsed.map((obj, i) => (
                          <tr key={obj.number + obj.client} style={{ background: i % 2 === 0 ? '#fff' : '#f6fafd', borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '1rem' }}>{obj.number}</td>
                            <td style={{ padding: '1rem' }}>{obj.client}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Assign Modal */}
      {assignModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(30,40,60,0.25)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '2.2rem 2.5rem', minWidth: 340, boxShadow: '0 4px 24px rgba(14,66,106,0.13)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: MEMO_ORANGE, fontWeight: 800, fontSize: '1.25rem', marginBottom: 18 }}>Nummer toewijzen aan klant</h3>
            <input
              type="text"
              placeholder="Friendly name (klantnaam)"
              value={assignName}
              onChange={e => setAssignName(e.target.value)}
              style={{ padding: '0.8em 1.2em', borderRadius: 8, border: '1.5px solid #dbe6ef', fontSize: '1.08em', marginBottom: 18, width: '100%' }}
              disabled={assignLoading}
              autoFocus
            />
            {assignError && <div style={{ color: '#d32f2f', fontWeight: 700, marginBottom: 10 }}>{assignError}</div>}
            {assignSuccess && <div style={{ color: MEMO_GREEN, fontWeight: 700, marginBottom: 10 }}>Nummer succesvol toegewezen!</div>}
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <button
                style={{ padding: '0.6em 1.5em', borderRadius: 7, border: 'none', background: MEMO_ORANGE, color: '#fff', fontWeight: 700, fontSize: '1em', cursor: assignLoading ? 'not-allowed' : 'pointer' }}
                disabled={assignLoading || !assignName.trim()}
                onClick={async () => {
                  setAssignLoading(true);
                  setAssignError("");
                  setAssignSuccess(false);
                  try {
                    const res = await fetch(`${getApiBaseUrl()}/assign-twilio-number`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        env,
                        phoneNumberSid: assigningSid,
                        friendlyName: assignName.trim(),
                        studioFlowSid: memoFlowSid,
                      }),
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      throw new Error(err.error || 'Fout bij toewijzen');
                    }
                    setAssignSuccess(true);
                    setTimeout(() => {
                      setAssignModalOpen(false);
                      setAssigningSid(null);
                      setAssignName("");
                      setAssignError("");
                      setAssignSuccess(false);
                      // Refresh numbers
                      setLoading(true);
                      fetch(`${getApiBaseUrl()}/twilio-phone-numbers?env=${env}`)
                        .then(r => r.json())
                        .then(setNumbers)
                        .finally(() => setLoading(false));
                    }, 1200);
                  } catch (err) {
                    setAssignError(err.message);
                  } finally {
                    setAssignLoading(false);
                  }
                }}
              >
                Bevestigen
              </button>
              <button
                style={{ padding: '0.6em 1.5em', borderRadius: 7, border: 'none', background: '#eee', color: '#333', fontWeight: 700, fontSize: '1em', cursor: assignLoading ? 'not-allowed' : 'pointer' }}
                disabled={assignLoading}
                onClick={() => {
                  setAssignModalOpen(false);
                  setAssigningSid(null);
                  setAssignName("");
                  setAssignError("");
                  setAssignSuccess(false);
                }}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Telefoonnummers;
